import { motion, HTMLMotionProps } from 'framer-motion';
import { ReactNode, useMemo, Children } from 'react';
import { staggerContainer } from '@/lib/motion/variants';
import { useReducedMotion } from '@/lib/motion/hooks/use-reduced-motion';

interface StaggerContainerProps extends Omit<HTMLMotionProps<'div'>, 'children' | 'viewport'> {
  children: ReactNode;
  /** Delay between each child animation */
  staggerDelay?: number;
  /** Initial delay before stagger begins */
  delayChildren?: number;
  className?: string;
  /** Whether to animate when element enters viewport */
  viewport?: boolean;
}

export function StaggerContainer({
  children,
  staggerDelay = 0.05,
  delayChildren = 0.1,
  className,
  viewport = false,
  ...props
}: StaggerContainerProps) {
  const reducedMotion = useReducedMotion();

  // Create a key based on children to detect when they change
  // This forces the component to remount and re-trigger animation when children change
  const containerKey = useMemo(() => {
    const childrenCount = Children.count(children);
    if (childrenCount === 0) {
      return 'no-children';
    }
    
    // Try to extract keys from children for more accurate change detection
    const childKeys: string[] = [];
    Children.forEach(children, (child: any) => {
      if (child && typeof child === 'object' && 'key' in child) {
        childKeys.push(String(child.key || ''));
      }
    });
    
    // Use count and keys to create a unique identifier
    return `${childrenCount}-${childKeys.join('-')}`;
  }, [children]);

  if (reducedMotion) {
    return <div className={className}>{children}</div>;
  }

  const variants = {
    ...staggerContainer,
    visible: {
      ...staggerContainer.visible,
      transition: {
        staggerChildren: staggerDelay,
        delayChildren,
      },
    },
  };

  const animateProps = viewport
    ? {
        initial: 'hidden',
        whileInView: 'visible',
        viewport: { once: true, margin: '-50px' },
      }
    : {
        initial: 'hidden',
        animate: 'visible',
      };

  return (
    <motion.div
      key={containerKey}
      variants={variants}
      {...animateProps}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}
